import { UserService } from 'src/app/core/service/user.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { Component, OnInit } from '@angular/core';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { EmailTemplateEdit } from '../model/email-template';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { EmailTemplateService } from 'src/app/core/service/email-template.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { Base64 } from 'js-base64';

@Component({
  standalone: false,
  selector: 'app-email-template-list',
  templateUrl: './email-template-list.component.html',
  styleUrls: ['./email-template-list.component.scss']
})
export class EmailTemplateListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public enableAdvanced = false;
  public count: number;
  public editDataItem: EmailTemplateEdit;
  public filterText = '';
  public preferredLanguages: Array<{ text: string; value: string }>;
  public templateTypes: Array<{ text: string; value: string }>;
  public templateMimeTypes: Array<{ text: string; value: string }>;
  public isNew = false;
  public communityEnabled = false;
  public selectedEmailTemplate: EmailTemplateEdit;
  public deleteDialogOpened = false;

  /**
   * Constructor
   */
  constructor(
    private emailTemplateService: EmailTemplateService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private userService: UserService,
    private settingService: SettingService
  )
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  public ngOnInit(): void
  {
    this.preferredLanguages = this.userService.preferredLanguages;
    this.templateTypes = this.emailTemplateService.templateTypes;
    this.templateMimeTypes = this.emailTemplateService.templateMimeTypes;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
    setTimeout(() =>
    {
      this.loadGridData();
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  public editHandler(id: number): void
  {
    this.emailTemplateService.getEmailTemplateById(id).subscribe({
      next: (data) =>
      {
        this.isNew = false;
        this.setDisplayTexts(data);
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(emailTemplate: EmailTemplateEdit): void
  {
    const objectToPersist = emailTemplate;
    objectToPersist.id = emailTemplate.id;
    objectToPersist.content = Base64.encode(emailTemplate.content);
    objectToPersist.type = emailTemplate.typeCode;
    objectToPersist.language = emailTemplate.language;
    objectToPersist.mimeType = emailTemplate.mimeTypeCode;

    delete objectToPersist.mimeTypeCode;
    delete objectToPersist.typeCode;

    this.emailTemplateService.saveEmailTemplate(objectToPersist, this.isNew, emailTemplate.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedDate into Date object
        data.lastModifiedDate = new Date(data.lastModifiedDate);
        this.setDisplayTexts(data);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.logger.info('saved', emailTemplate);

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('EMAIL-TEMPLATE-SAVED-SUCCESSFULLY');
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state, 'type');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Add handler for new template. A new template will be available only if community is enabled.
   * The only new template available is sending invitation email.
   */
  public addHandler(): void
  {
    this.isNew = true;
    const newEmailTemplate = new EmailTemplateEdit();
    newEmailTemplate.type = 'CommunityInvite';
    newEmailTemplate.language = 'English';
    newEmailTemplate.mimeType = 'TEXT';
    newEmailTemplate.content = '';
    this.setDisplayTexts(newEmailTemplate);
    this.editDataItem = newEmailTemplate;
  }

  /**
   * Sets the email template to delete, and opens the delete dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.emailTemplateService.emailTemplateInUse(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('EMAIL-TEMPLATE-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.emailTemplateService.getEmailTemplateById(id).subscribe({
            next: (data) =>
            {
              this.selectedEmailTemplate = data as EmailTemplateEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public deleteEmailTemplate(): void
  {
    // Proceed with the deletion
    this.emailTemplateService.deleteEmailTemplate(this.selectedEmailTemplate.id).subscribe({
      next: () =>
      {
        this.view.data = this.view.data.filter(e => e.id !== this.selectedEmailTemplate.id);
        this.view.refreshView();
        this.count = this.view.data.length;
        this.messageHandlerService.displaySuccess('EMAIL-TEMPLATE-DELETED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.deleteDialogOpened = false;
      }
    });
  }

  public isAllowAddUpdate(dataItem: any): boolean
  {
    return dataItem !== undefined && dataItem.typeCode === 'CommunityInvite' && dataItem.createdBy !== 'SYSTEM';
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.emailTemplateService.getEmailTemplatesList().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.lastModifiedDate = new Date(d.lastModifiedDate);
          this.setDisplayTexts(d);
        });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'type', dir: 'asc' }]);
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  private setDisplayTexts(d: any): void
  {
    // Keep original codes (used in edit modal)
    d.mimeTypeCode = d.mimeType;
    d.typeCode = d.type;
    d.language = this.userService.getLanguageText(d.language);
    d.type = this.emailTemplateService.getEmailTemplateTypeText(d.type);
    d.mimeType = this.emailTemplateService.getEmailTemplateMimeTypeText(d.mimeType);
  }
}
